/* This example requires Tailwind CSS v2.0+ */
import React, { Fragment, useEffect, useState, useRef, useContext } from 'react'
import { Dialog, Transition } from '@headlessui/react'
import { CheckIcon, XIcon } from '@heroicons/react/outline'
import { LinkIcon } from '@heroicons/react/solid'
import ReactGA from "react-ga4"
import { useHistory } from "react-router-dom";

import ProductName from './slides/ProductName'
import AdditionalDetails from './slides/AdditionalDetails'
import Login from './slides/Login'
import SecureSavings from './slides/SecureSavings'
import GuestRequest from './slides/GuestRequest'
import AuthService from '../services/AuthService'
import UserService from '../services/UserService'
import { NotificationContext, useNotification } from '../providers/NotificationProvider';
import { useUser } from '../providers/UserProvider'

const SubmitModal = (props) => {
  // const { open, setOpen, loadingBarRef } = props;
  const { loadingBarRef } = props;
  const [user, userDispatch] = useUser();
  const [error, setError] = useState(null);

  const [open, setOpen] = useState(user.productRequestShowModal);

  useEffect(() => {
    setOpen(user.productRequestShowModal)
  }, [user]);

  const history = useHistory();

  const [state, dispatch] = useContext(NotificationContext);
  const notificationRef = useRef(null);

  const [currentSlide, setCurrentSlide] = useState(0);

  const getUserData = async (guest = false) => {

    if (guest)
      return { ...user }

    let userData;
    const token = localStorage.getItem("AUTH_TOKEN");

    // do Me call
    const userResponse = await UserService.me(token);

    if (userResponse.data.errors || userResponse.data.data.me === null) {
      throw Error('Error fetching user data');
    }

    userData = {
      ...user,
      profile: {
        id: userResponse.data.data.me.id,
        name: userResponse.data.data.me.name,
        email: userResponse.data.data.me.email,
      }
    };

    return userData;
  };

  useEffect(() => {
    if (open) {
      fireGAEvent(slides[currentSlide].gaEvent.action, slides[currentSlide].gaEvent.details);
    }
  }, [open]);

  useEffect(() => {
    if (open) {
      fireGAEvent(slides[currentSlide].gaEvent.action, slides[currentSlide].gaEvent.details);
    }
  }, [currentSlide]);

  const submitProductRequest = async (data, guest = false) => {
    let userData, token = '';

    try {
      userData = await getUserData(guest);

      if (!guest)
        token = localStorage.getItem("AUTH_TOKEN");

      // fix timing issue with dispatch
      if (data && data.hasOwnProperty('details'))
        userData.productRequestDetails = data.details;
      
      if (data && data.hasOwnProperty('urgency'))
        userData.productRequestUrgency = data.urgency;

      if (data && data.hasOwnProperty('guestName'))
        userData.guestName = data.guestName;
      
      if (data && data.hasOwnProperty('guestEmail'))
        userData.guestEmail = data.guestEmail;

      const response = await UserService.createProductRequest(userData, token, guest);
      
      let responseData = response.data.data.submitProductRequest

      if (responseData.success) {
        // open modal to slide 3
        setCurrentSlide(3);
      } else {
        console.log('Show Error');
      }

    } catch (error) {
      console.error('Show Error', error);
    }
  };

  const slides = [
    {
      title: 'Step 1',
      body: <ProductName submitAction={() => {
        setCurrentSlide(1)
        fireGAEvent(slides[0].gaEvent.buttons.submitAction.action, slides[0].gaEvent.buttons.submitAction.details)
      }} />,
      gaEvent: {
        action: 'modal_opened',
        details: {
          slide: `product_name`,
        },
        buttons: {
          submitAction: {
            action: 'button_clicked',
            details: {
              name: `lets_go`,
            },
          },
        }
      }
    },
    {
      title: 'Step 2',
      body: <AdditionalDetails goBack={() => {
        setCurrentSlide(0)
        fireGAEvent(slides[1].gaEvent.buttons.goBack.action, slides[1].gaEvent.buttons.goBack.details)
      }} 
      submitAction={async (data) => {
        fireGAEvent(slides[1].gaEvent.buttons.submitAction.action, slides[1].gaEvent.buttons.submitAction.details)
        if (user.loggedIn) {
          loadingBarRef.current.continuousStart();
          await submitProductRequest(data)
          loadingBarRef.current.complete();
        } else {
          setCurrentSlide(2)
        }
        return;
      }} />,
      gaEvent: {
        action: 'modal_opened',
        details: {
          slide: `additional_details`,
        },
        buttons: {
          submitAction: {
            action: 'button_clicked',
            details: {
              name: `continue`,
            },
          },
          goBack: {
            action: 'button_clicked',
            details: {
              name: `go_back`,
            },
          },
        }
      }
    },
    {
      title: 'Step 3',
      body: <Login user={user} error={error} setError={setError} 
      guestRequest={() => {
        setCurrentSlide(4)
        fireGAEvent(slides[2].gaEvent.buttons.guestRequest.action, slides[2].gaEvent.buttons.guestRequest.details)
      }} 
      goBack={() => {
        setCurrentSlide(1)
        fireGAEvent(slides[2].gaEvent.buttons.goBack.action, slides[2].gaEvent.buttons.goBack.details)
      }} 
      submitAction={async (username, password) => {
        fireGAEvent(slides[2].gaEvent.buttons.submitAction.action, slides[2].gaEvent.buttons.submitAction.details)
        loadingBarRef.current.continuousStart();

        if (username && password) {
          const response = await AuthService.getTokenViaCredentials(username, password);

          let login = response.data.data.login;
          
          if (login.success) {
            dispatch({
              type: "notification/message",
              payload: { message: "Login Successful" }
            });

            userDispatch({ type: "productRequest/loggedIn", payload: true });
            localStorage.setItem('AUTH_TOKEN', login.token)

            ReactGA.gtag("event", "login");
            // save slide 3
            await submitProductRequest();

          } else {
            notificationRef.current = setTimeout(() => {
              dispatch({ type: "notification/hide" });
            }, 5000);
            dispatch({
              type: "notification/error",
              payload: { message: login.message }
            });
            setError(login.message)
            return;
          }
        }

        loadingBarRef.current.complete();
      }} />,
      gaEvent: {
        action: 'modal_opened',
        details: {
          slide: `login`,
        },
        buttons: {
          submitAction: {
            action: 'button_clicked',
            details: {
              name: `submit_request`,
            },
          },
          goBack: {
            action: 'button_clicked',
            details: {
              name: `go_back`,
            },
          },
          guestRequest: {
            action: 'button_clicked',
            details: {
              name: `guest_request`,
            },
          }
        }
      }
    },
    {
      title: 'Step 4',
      body: <SecureSavings submitAction={() => {
        setCurrentSlide(0)
        fireGAEvent(slides[3].gaEvent.buttons.submitAction.action, slides[3].gaEvent.buttons.submitAction.details)
      }} />,
      gaEvent: {
        action: 'modal_opened',
        details: {
          slide: `request_recieved`,
        },
        buttons: {
          submitAction: {
            action: 'button_clicked',
            details: {
              name: `make_another_request`,
            },
          },
        }
      }
    },
    {
      title: 'Step 5 (guest)',
      body: <GuestRequest user={user} error={error} setError={setError} 
      goBack={() => {
        setCurrentSlide(2)
        fireGAEvent(slides[4].gaEvent.buttons.goBack.action, slides[4].gaEvent.buttons.goBack.details)
      }} 
      submitAction={
        async (guestName, guestEmail) => {
          fireGAEvent(slides[4].gaEvent.buttons.submitAction.action, slides[4].gaEvent.buttons.submitAction.details)
          loadingBarRef.current.continuousStart();

          await submitProductRequest({guestName, guestEmail}, true);

          loadingBarRef.current.complete();
        }
      } />,
      gaEvent: {
        action: 'modal_opened',
        details: {
          slide: `guest_request`,
        },
        buttons: {
          submitAction: {
            action: 'button_clicked',
            details: {
              name: `submit_request`,
            },
          },
          goBack: {
            action: 'button_clicked',
            details: {
              name: `go_back`,
            },
          },
        }
      }
    },
  ];

  const fireGAEvent = (action, details = {}) => {
    const event = 'product_request';
    const eventProps = {
      action,
      details,
    };
    // console.log('GA EVENT FIRED:', event, eventProps)
    
    window.dataLayer.push({
      event,
      eventProps,
    });
  };

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog as="div" className="relative z-70" onClick={() => console.log('click background')} onClose={setOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed z-10 inset-0 overflow-y-auto">
          <div className="flex items-end sm:items-center justify-center min-h-full p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel>
                <div className='flex flex-row justify-end'>
                  <button 
                    type="button" 
                    className="text-darkIcon text-xxs bg-grey-buttonText inline-flex items-center p-3.5 border border-transparent rounded-full shadow-sm focus:outline-none"
                    onClick={() => {
                      console.log('on close')
                      // check if user has logged in using the modal.
                      if (user.productRequestLoggedIn) {
                        userDispatch({ type: "productRequest/loggedIn", payload: false });
                        history.go('/marketplace')
                      }
                      // if so, history.go('/marketplace')
                      
                      setOpen(false)
                      fireGAEvent('modal_closed');
                    }}
                  >
                    <XIcon className="h-6 w-6" aria-hidden="true" />
                  </button>
                </div>
                <div className="border-convoy-modalBorder border-4 rounded-24 relative bg-white rounded-lg px-4 py-6 xl:px-6 xl:py-8 text-left overflow-hidden transform transition-all sm:my-4 sm:max-w-sm sm:w-full modal-shadow xl:max-w-product-request-modal">
                  {slides[currentSlide].body}
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  )
};

export default SubmitModal;
